
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Calendar, Wheat, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useGetHarvests } from '@/hooks/use-harvest';
import { format } from 'date-fns';

const CoordinatorHarvestManagement: React.FC = () => {
  const { data: harvests = [], isLoading } = useGetHarvests();
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredHarvests = harvests.filter(harvest =>
    harvest.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    harvest.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const upcomingHarvests = harvests.filter(h => h.status === 'planned').length;
  const inProgressHarvests = harvests.filter(h => h.status === 'in_progress').length;
  const completedHarvests = harvests.filter(h => h.status === 'completed').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned':
        return 'secondary';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading harvests...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Harvest Management</h1>
          <p className="text-muted-foreground">Schedule and track harvest activities</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/harvest-calendar">
              <Calendar className="h-4 w-4 mr-2" />
              View Calendar
            </Link>
          </Button>
          <Button asChild>
            <Link to="/harvest-form">
              <Plus className="h-4 w-4 mr-2" />
              New Harvest
            </Link>
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search harvests by product or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{upcomingHarvests}</div>
            <p className="text-sm text-muted-foreground">Planned</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{inProgressHarvests}</div>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{completedHarvests}</div>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{harvests.length}</div>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Harvests List */}
      <div className="space-y-4">
        {filteredHarvests.map((harvest) => (
          <Card key={harvest.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Wheat className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">
                      {harvest.product_name || 'Unknown Product'}
                    </h3>
                    <Badge variant={getStatusColor(harvest.status)}>
                      {harvest.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {harvest.expected_date && (
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                        Expected: {format(new Date(harvest.expected_date), 'MMM dd, yyyy')}
                      </div>
                    )}
                    {harvest.actual_date && (
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                        Actual: {format(new Date(harvest.actual_date), 'MMM dd, yyyy')}
                      </div>
                    )}
                    {harvest.expected_quantity && (
                      <div>
                        Expected: {harvest.expected_quantity} kg
                      </div>
                    )}
                    {harvest.actual_quantity && (
                      <div>
                        Actual: {harvest.actual_quantity} kg
                      </div>
                    )}
                    {harvest.quality_grade && (
                      <div>
                        Quality: {harvest.quality_grade}
                      </div>
                    )}
                  </div>

                  {harvest.notes && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {harvest.notes}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 ml-4">
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/harvest-form?edit=${harvest.id}`}>
                      Edit
                    </Link>
                  </Button>
                  <Button asChild size="sm">
                    <Link to={`/harvests`}>
                      View Details
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredHarvests.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Wheat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No harvests found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'Start by creating your first harvest schedule'}
            </p>
            <Button asChild>
              <Link to="/harvest-form">
                <Plus className="h-4 w-4 mr-2" />
                Create First Harvest
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CoordinatorHarvestManagement;
